import {
  DashboardState,
  EActionCategory,
  EActionSubCategory,
  EApiResponseContentType,
  EDatasourceType,
  EPropertyType,
  ESdChangeReason,
  ESymbolCategory,
  EUserAccessPart,
  IAction,
  IActionDefinitionDto,
  IActionParameter,
  IDatasourceDto,
  IDisplayItem,
  IDisplayItemComponent,
  IEvent,
  IScreenDesign,
  ISymbol,
  SpecialDIIds
} from "@kuika/kuika-cl-model";
import type { CascaderOptionType, CascaderValueType } from "antd/es/cascader";
import { Guid } from "guid-typescript";
import _ from "lodash";
import React from "react";
import { DragDropContext } from "react-beautiful-dnd";
import { connect } from "react-redux";
import LocalStorageService from "../../../../auth/local-storage-service";
import { IActionClipboard } from "../../../../models/interface/action-clipboard";
import ActionService from "../../../../services/action-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import {
  isActionDefinitionVisible,
  isDocumentParameterSourceAction,
  type ActionFilterContext
} from "../../../../shared/utility/action-definition-filter";
import { ActionDefinitionLookup } from "../../../../shared/utility/action-definition-lookup";
import { getActionReturnTypeIconName, isVoidReturnType } from "../../../../shared/utility/action-definition-summary";
import {
  isActionDefinitionPlatformCompatible,
  type AttachablePlatformContext
} from "../../../../shared/utility/bindables/attachable-actions";
import { getCascaderOptionSearchText } from "../../../../shared/utility/cascader-search";
import { getDatasourceLabel } from "../../../../shared/utility/datasource-name-qualifier";
import { rebindDesignerPastedAction } from "../../../../shared/utility/designer-action-clipboard-actions";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { MiddleTruncate } from "../../../../shared/utility/middle-truncate";
import { isOfflineCompatibleActionDefinition } from "../../../../shared/utility/offline-action-compatibility";
import { PropertySdHelper } from "../../../../shared/utility/property-sd-helper";
import { ScreenDesignHelper } from "../../../../shared/utility/screen-design-helper";
import { showDomainAccessRequiredModal } from "../../../../store/domain-access-modal/actions";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaInfoBox from "../../../ui-kit/kuika-info-box/kuika-info-box";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { getActionCategoryIconName } from "./action-category-icons";
import {
  EActionNodeCategory,
  type IActionPickerOption,
  type IActionPickerOptionMeta
} from "./action-picker-popup/action-picker-node";
import ActionPickerPopup from "./action-picker-popup/action-picker-popup";
import ActionSectionEmptyState from "./action-section-empty-state";
import { ActionSectionAddButton, ActionSectionTitle } from "./action-section-header";
import "./action-section.scss";
import { EventPanel } from "./event-panel";
import { focusFirstEditableControl } from "./focus-first-property-control";
import { consumePendingOpenEventRequest, IOpenEventRequest, subscribeOpenEventRequest } from "./open-event-in-panel";

// Re-exported: the nested pickers and the workflow's dynamic assignment import it from here.
export { EActionNodeCategory } from "./action-picker-popup/action-picker-node";

/**
 * Highest action count a category row spells out. The row is bounded, so an unbounded figure buys
 * width from the name beside it; past this ceiling the exact number tells nobody anything they can
 * act on, and "999+" costs a fixed four characters forever.
 */
const CATEGORY_COUNT_CEILING: number = 999;

const formatCategoryCount = (count: number): string =>
  count > CATEGORY_COUNT_CEILING ? `${CATEGORY_COUNT_CEILING}+` : `${count}`;

export interface IActionPickerState {
  options: IActionPickerOption[];
  popupVisible: boolean;
  clipboardValue: string | undefined;
  eventItems: CascaderOptionType[];
}

export interface IActionPickerProps {
  showDomainAccessRequiredModal: (actionName: string | string[]) => void;
  preventAddAction?: boolean;
  preventActionRemoval?: boolean;
  customHeader?: React.ReactNode;
  /**
   * Suppress the picker's own title so a host panel can supply its own section head
   * (workflow panels only — the design-mode "Actions" title is unaffected).
   */
  hideHeader?: boolean;
}

export interface IOtherDIsNonEmptyEvents {
  diId: number;
  diFriendlyName?: string;
  eventNames: string[];
}

interface IActionDatasourceOption {
  label: string;
  value: string;
}

// KP Actions Panel redesign (Figma node 476-87316): every category row carries a 16px glyph. The map
// lives in action-category-icons.ts, shared with the action card's kind tile.

let defaultState = {} as IActionPickerState;

class ActionPicker extends PureSdComponent<IActionPickerProps & IWithSDProps & IWithContextProp, IActionPickerState> {
  propertySdHelper: PropertySdHelper;

  bottomRef: HTMLDivElement | null | undefined;

  // Element-labelled options (return-type icons, category rows) carry their plain name in
  // searchLabel — sorting/searching must never fall back to a GUID value. Shared with the four
  // nested action pickers so all five agree on what an option is called.
  getStringValueForSort = (item: CascaderOptionType): string => getCascaderOptionSearchText(item);

  constructor(props: IActionPickerProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.propertySdHelper = new PropertySdHelper(this.getScreenDesignContextProviderValues);
    this.state = {
      options: [],
      popupVisible: false,
      clipboardValue: undefined,
      eventItems: []
    };
    defaultState = this.state;
  }

  componentDidMount = () => {
    // Generate event items and store in state
    const eventItems = this.generateEventItems();
    this.setState({ eventItems });

    this._unsubscribeOpenEvent = subscribeOpenEventRequest(this.handleOpenEventRequest);
    const pending: IOpenEventRequest | undefined = consumePendingOpenEventRequest();
    if (pending) {
      this.handleOpenEventRequest(pending);
    }
  };

  _unsubscribeOpenEvent: (() => void) | null = null;

  componentWillUnmount(): void {
    this._unsubscribeOpenEvent?.();
    this._unsubscribeOpenEvent = null;
  }

  /**
   * The canvas selection bar's event chip asked for one event (open-event-in-panel.ts): scroll its
   * action list into view and flash it; for "add", open the add-action picker too. Only the screen
   * designer's picker for that same component answers — other hosts (scheduled jobs, APIs, …) ignore it.
   */
  handleOpenEventRequest = (request: IOpenEventRequest): boolean => {
    if (this.props.sd.dashboardState !== DashboardState.design) return false;
    const selected: IDisplayItem | undefined = this.getSelectedDisplayItem();
    if (selected?.id?.toString() !== request.kuikaId) return false;
    requestAnimationFrame((): void => {
      const list: Element | null = document.querySelector(`[data-testid="actionList_${request.eventName}"]`);
      const target: Element | null = list ?? document.querySelector(".kuika-action-section__header");
      target?.scrollIntoView({ block: "center", behavior: "smooth" });
      if (list instanceof HTMLElement) {
        list.classList.remove("kuika-action-section__events-list--flash");
        // Restart the animation when the same event is requested twice in a row.
        void list.offsetWidth;
        list.classList.add("kuika-action-section__events-list--flash");
      }
      if (request.mode === "add" && !this.state.popupVisible) {
        this.getOptions(true);
      }
    });
    return true;
  };

  componentDidUpdate = (prevProps: IActionPickerProps & IWithSDProps & IWithContextProp) => {
    if (this.props.sd.selectedKuikaIds[0] !== prevProps.sd.selectedKuikaIds[0]) {
      this.setState({ ...defaultState });
    }

    // Update event items when relevant data changes
    const needsEventItemsUpdate =
      prevProps.sd.selectedKuikaIds[0] !== this.props.sd.selectedKuikaIds[0] ||
      prevProps.sd.getSelectedSj()?.id !== this.props.sd.getSelectedSj()?.id ||
      prevProps.sd.getSelectedApi()?.id !== this.props.sd.getSelectedApi()?.id ||
      prevProps.sd.getSelectedApiMethod()?.id !== this.props.sd.getSelectedApiMethod()?.id ||
      prevProps.sd.selectedWf?.id !== this.props.sd.selectedWf?.id ||
      prevProps.sd.getSelectedWfStep()?.id !== this.props.sd.getSelectedWfStep()?.id ||
      prevProps.sd.activeEditingDatasource?.id !== this.props.sd.activeEditingDatasource?.id ||
      prevProps.sd.getSelectedBluetoothMethod()?.id !== this.props.sd.getSelectedBluetoothMethod()?.id ||
      prevProps.sd.dashboardState !== this.props.sd.dashboardState;

    if (needsEventItemsUpdate) {
      const eventItems = this.generateEventItems();
      this.setState({ eventItems });
    }
  };

  getScreenDesignContextProviderValues = () => {
    return this.props.sd;
  };

  /**
   * Opens and closes the popup — the one path for the "+", the empty state's "Add action" and the
   * popup's own dismissals. The tree is built on open, so the popup always lists what is offerable now.
   */
  getOptions = (popupVisible: boolean): void => {
    if (popupVisible === true) {
      const sJob = this.props.sd.getSelectedSj();
      const apiMethod = this.props.sd.getSelectedApiMethod();
      const wfStep = this.props.sd.getSelectedWfStep();
      const bleMethod = this.props.sd.getSelectedBluetoothMethod();
      let opts: IActionPickerOption[];
      if (sJob || apiMethod || wfStep || bleMethod) {
        opts = this.generateActionDefinitionItems();
      } else opts = this.generateEventItems();

      this.setState({ options: opts, popupVisible: true });
    } else if (this.state.popupVisible) {
      this.setState({ popupVisible: false });
    }
  };

  /** The popup's pick: the same `selectedOptions` path the Cascader's onChange delivered. */
  handlePick = (selectedOptions: CascaderOptionType[]): void => {
    const values: CascaderValueType = selectedOptions.map((option: CascaderOptionType) => option.value ?? "");
    this.handleAddAction(values, selectedOptions);
  };

  actionDefinitionLookup: ActionDefinitionLookup = new ActionDefinitionLookup();

  getActionDefinitionById = (id: string): IActionDefinitionDto | undefined =>
    this.actionDefinitionLookup.get(this.props.sd.actionDefinitions, id);

  /** Mirrors the domain gate in handleAddAction, so the preview says it before the click does. */
  getActionLockReason = (definition: IActionDefinitionDto): string | undefined => {
    if (
      definition.actionCategory === EActionCategory.ProcessAutomation &&
      KMainFunctions.isDomainHasAccess("Workflow") === false
    ) {
      return "Needs the Workflow domain. Adding this action asks for access.";
    }
    return undefined;
  };

  generateEventItems = (): CascaderOptionType[] => {
    const di = this.getSelectedDisplayItem();
    const sJob = this.props.sd.getSelectedSj();
    const api = this.props.sd.getSelectedApi();
    const apiMethod = this.props.sd.getSelectedApiMethod();
    const wf = this.props.sd.selectedWf;
    const wfStep = this.props.sd.getSelectedWfStep();
    const ds = this.props.sd.activeEditingDatasource;
    const bleMethod = this.props.sd.getSelectedBluetoothMethod();

    if (sJob && this.props.sd.dashboardState === DashboardState.scheduledJobs) {
      const scheduledInitEvent: CascaderOptionType = {
        category: EActionNodeCategory.event,
        label: "Scheduled Actions",
        value: "pageInit",
        children: this.generateActionDefinitionItems()
      };
      return [scheduledInitEvent];
    }
    if (this.props.sd.isDocumentParameterAuthoringActive()) {
      const documentParametersInitEvent: CascaderOptionType = {
        category: EActionNodeCategory.event,
        label: "Document Parameter Actions",
        value: "pageInit",
        children: this.generateActionDefinitionItems()
      };
      return [documentParametersInitEvent];
    }
    if (api && apiMethod && this.props.sd.dashboardState === DashboardState.apiManagement) {
      const apiInitEvent: CascaderOptionType = {
        category: EActionNodeCategory.event,
        label: "Api Actions",
        value: "pageInit",
        children: this.generateActionDefinitionItems()
      };
      return [apiInitEvent];
    }
    if (wf && wfStep && this.props.sd.dashboardState === DashboardState.workFlow) {
      const workflowInitEvent: CascaderOptionType = {
        category: EActionNodeCategory.event,
        label: "Process Automation Actions",
        value: "pageInit",
        children: this.generateActionDefinitionItems()
      };
      return [workflowInitEvent];
    }

    if (ds?.bluetoothInfo && bleMethod && this.props.sd.dashboardState === DashboardState.dataDiagramScheme) {
      const bleMethodInitEvent: CascaderOptionType = {
        category: EActionNodeCategory.event,
        label: "Bluetooth Actions",
        value: "pageInit",
        children: this.generateActionDefinitionItems()
      };

      return [bleMethodInitEvent];
    }
    if (!di || di.id == -1) {
      const pageInitEvent: CascaderOptionType = {
        category: EActionNodeCategory.event,
        label: "Initial Actions",
        value: "pageInit",
        children: this.generateActionDefinitionItems(true)
      };
      const recurringEvent: CascaderOptionType = {
        category: EActionNodeCategory.action,
        label: "Recurring Actions",
        value: "Recurring",
        children: []
      };
      return [pageInitEvent, recurringEvent];
    }

    const options: CascaderOptionType[] = this.getEventOptions();
    return options;
  };

  getEventOptions = (): CascaderOptionType[] => {
    const options: CascaderOptionType[] = [];
    const di = this.getSelectedDisplayItem();

    if (di?.id === SpecialDIIds.rootDI) {
      return [
        {
          category: EActionNodeCategory.event,
          label: "Initial Actions",
          value: "pageInit",
          children: []
        },
        {
          category: EActionNodeCategory.event,
          label: "Recurring Actions",
          value: "Recurring",
          children: []
        }
      ];
    }

    if (di) {
      const component: IDisplayItemComponent = di.item;
      component.properties
        .filter((p) => p.selectedType === EPropertyType.event)
        .forEach((event) => {
          const evt: CascaderOptionType = {
            category: EActionNodeCategory.event,
            label: event.name,
            value: event.name,
            children: this.generateActionDefinitionItems()
          };
          options.push(evt);
        });
    }
    return options;
  };

  getEvents = (): IEvent[] => {
    const di = this.getSelectedDisplayItem();
    if (!di) {
      return [];
    }
    const component: IDisplayItemComponent = di.item;
    if (component.events) {
      return component.events;
    }
    return [];
  };

  getScheduledJobEvents = (): IEvent | undefined => {
    const sj = this.props.sd.getSelectedSj();

    if (!sj) {
      return;
    }
    const { json } = sj;
    if (json?.actions) {
      return json;
    }
  };

  /**
   * Source actions for the Document Parameters tool (KOA-17254). The tool itself is the host — there is no
   * sub-entity to select — so the event is available whenever that surface is open.
   */
  getDocumentParameterEvents = (): IEvent | undefined => {
    const isAuthoring: boolean = this.props.sd.isDocumentParameterAuthoringActive();
    if (!isAuthoring) return;
    return this.props.sd.documentParameterEvent;
  };

  getApiEvents = (): IEvent | undefined => {
    const api = this.props.sd.getSelectedApi();
    const method = this.props.sd.getSelectedApiMethod();

    if (!api || !method) {
      return;
    }
    if (method && method?.actions) {
      return method as any;
    }
  };

  shouldShowHtmlReturnTypeWarning = (): boolean => {
    if (this.props.sd.dashboardState !== DashboardState.apiManagement) {
      return false;
    }

    const selectedApiMethod = this.props.sd.getSelectedApiMethod();

    if (!selectedApiMethod) {
      return false;
    }

    // Only show warning if response content type is HTML
    if (selectedApiMethod.responseContentType !== EApiResponseContentType.TextHtml) {
      return false;
    }

    // Get the last action
    const actions = selectedApiMethod.actions;
    if (!actions || actions.length === 0) {
      return true; // Show warning if no actions
    }

    const lastAction = actions[actions.length - 1];
    if (!lastAction?.actionDefinitionId) {
      return true; // Show warning if last action has no definition
    }

    // Find the action definition to get the return type
    const actionDefinition = this.props.sd.actionDefinitions.find((def) => def.id === lastAction.actionDefinitionId);

    if (!actionDefinition) {
      return true; // Show warning if definition not found
    }

    // Check if return type is string (case-insensitive)
    const returnTypeName = actionDefinition.returnTypeName?.toLowerCase();
    return returnTypeName !== "string";
  };

  getWfStepEvents = (): IEvent | undefined => {
    const wf = this.props.sd.selectedWf;
    const step = this.props.sd.getSelectedWfStep();

    if (!wf || !step) {
      return;
    }

    if (step && step?.actions) {
      // TODO: Remove any
      return step as any;
    }
  };

  getBleEvents = (): IEvent | undefined => {
    const method = this.props.sd.getSelectedBluetoothMethod();
    if (!method) return;
    if (method && method.bluetoothInfo?.actions) {
      return method.bluetoothInfo as any;
    }
  };

  /**
   * The "Actions" header's count badge. Mirrors the exact branch predicates the render() ternary
   * below uses to pick which EventPanel(s) to show — not a bare `??` over the getters, because
   * several of them (getSelectedApi/Method, getSelectedWfStep, getSelectedBluetoothMethod) can
   * still return a previously-selected value after the user navigates to a different
   * dashboardState, which is exactly why that ternary re-checks dashboardState on every branch
   * instead of trusting the getters alone.
   */
  getActionCount = (
    documentParameterEvents: IEvent | undefined,
    sJobs: IEvent | undefined,
    apis: IEvent | undefined,
    wfStepEvents: IEvent | undefined,
    bleEvents: IEvent | undefined,
    events: IEvent[]
  ): number => {
    if (this.props.sd.isDocumentParameterAuthoringActive()) {
      return documentParameterEvents?.actions?.length ?? 0;
    }
    if (this.props.sd?.getSelectedSj()?.id && this.props.sd.dashboardState === DashboardState.scheduledJobs) {
      return sJobs?.actions?.length ?? 0;
    }
    if (
      this.props.sd?.getSelectedApi()?.id &&
      this.props.sd?.getSelectedApiMethod()?.id &&
      this.props.sd.dashboardState === DashboardState.apiManagement
    ) {
      return apis?.actions?.length ?? 0;
    }
    if (
      this.props.sd.selectedWf?.id &&
      this.props.sd.getSelectedWfStep()?.id &&
      this.props.sd.dashboardState === DashboardState.workFlow
    ) {
      return wfStepEvents?.actions?.length ?? 0;
    }
    if (
      this.props.sd.activeEditingDatasource?.bluetoothInfo &&
      this.props.sd.getSelectedBluetoothMethod()?.id &&
      this.props.sd.dashboardState === DashboardState.dataDiagramScheme
    ) {
      return bleEvents?.actions?.length ?? 0;
    }
    return events.reduce((sum: number, event: IEvent): number => sum + (event.actions?.length ?? 0), 0);
  };

  /**
   * The platform context that decides which actions this project may attach — the same one the component
   * palette and the visual symbol picker build. Applied at the picker so a Mobile (React Native) project's
   * event action list hides the Web-only actions it cannot run, the way the palette already hides Web-only
   * components. Being dashboard-aware, it keeps Web-only actions on the backend surfaces (scheduled jobs)
   * that deliberately need them, so it is robust to the `dontFilterByProjectType` load-time bypass.
   */
  getAttachablePlatformContext = (): AttachablePlatformContext => {
    return {
      projectType: this.props.sd.project.projectType,
      isWebScreen: this.props.sd.getIsWebScreen(),
      dashboardState: this.props.sd.dashboardState,
      isOfflineScreen: this.props.sd.project.offlineScreenId === KMainFunctions.getSdIdFromUrl()
    };
  };

  getActionDefinitionsByType = (category: EActionCategory, isInitialAction?: boolean): CascaderOptionType[] => {
    const hasAccessEditSystemAction = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.AddEditSystemActions,
      this.props.sd.project?.projectAccess
    );
    const hasAccessManageCustomAction = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.ManageCustomActions,
      this.props.sd.project?.projectAccess
    );

    if (category === EActionCategory.Custom && hasAccessManageCustomAction) return this.generateDataSources();

    // Payment and Trigger return before isActionDefinitionVisible is consulted, so a context that restricts which
    // actions are offerable has to be honoured here too — otherwise these two survive a filter that empties every
    // other category. Neither can ever fetch a row, so Document Parameters excludes them outright.
    if (this.props.sd.isDocumentParameterAuthoringActive()) {
      if (category === EActionCategory.Payment || category === EActionCategory.Trigger) return [];
    } else {
      if (category === EActionCategory.Payment) return this.generatePayments();
      if (category === EActionCategory.Trigger) return this.getTriggerCategoryDefinitions();
    }

    const ctx: ActionFilterContext = {
      dashboardState: this.props.sd.dashboardState,
      // Explicit rather than derived from the dashboard state: the same authoring UI also runs as a popup over the
      // design canvas, where the dashboard is still `design`.
      isDocumentParameterAuthoring: this.props.sd.isDocumentParameterAuthoringActive(),
      projectAccess: this.props.sd.project?.projectAccess,
      isBleServiceDataSource:
        this.props.sd.dashboardState === DashboardState.dataDiagramScheme &&
        this.props.sd.activeEditingDatasource?.datasourceType === EDatasourceType.BleService,
      isUserKuikaDeveloper: KMainFunctions.isUserKuikaDeveloper(this.props?.context.userInfo?.username),
      isCurrentProcessScreenRole: !!this.props.sd.project?.processStepScreenIds?.some(
        (p) => p.screenId === this.props.sd.sdId
      )
    };

    const platform: AttachablePlatformContext = this.getAttachablePlatformContext();
    let actionDefinitions = this.props.sd.actionDefinitions || [];
    actionDefinitions = actionDefinitions.filter(
      (x) =>
        x.actionCategory === category &&
        isActionDefinitionVisible(x, ctx) &&
        isActionDefinitionPlatformCompatible(x, platform) &&
        !(isInitialAction && (x.name === "GoToFullScreen" || x.name === "PhotoFromGallery")) &&
        // Exclude custom datasource actions from Other category — they are already shown under Custom
        !(category === EActionCategory.Other && x.kDatasource_Name && x.kDatasource_Name !== "SystemJsFunctions")
    );

    const groupedBySubcategory = new Map<EActionSubCategory, IActionDefinitionDto[]>();
    const nonGroupedActions: IActionDefinitionDto[] = [];

    actionDefinitions.forEach((action) => {
      if (action.actionSubCategory !== undefined && action.actionSubCategory !== EActionSubCategory.None) {
        if (!groupedBySubcategory.has(action.actionSubCategory)) {
          groupedBySubcategory.set(action.actionSubCategory, []);
        }
        groupedBySubcategory.get(action.actionSubCategory)?.push(action);
      } else {
        nonGroupedActions.push(action);
      }
    });

    const result: CascaderOptionType[] = [];

    groupedBySubcategory.forEach((actions, subcategory) => {
      const children: CascaderOptionType[] = actions
        .map((action) => {
          const formattedName: string = KMainFunctions.getFormattedSystemActionName(action);
          return {
            category: EActionNodeCategory.action,
            label: this.getActionOptionLabel(action, formattedName),
            searchLabel: formattedName,
            value: action.id,
            children: undefined
          };
        })
        .sort((a, b) =>
          this.getStringValueForSort(a).localeCompare(this.getStringValueForSort(b), undefined, {
            sensitivity: "base"
          })
        );

      result.push({
        category: EActionNodeCategory.actionCategory,
        label: EActionSubCategory[subcategory],
        value: `subcat_${subcategory}`,
        children
      });
    });

    // Add non-grouped actions
    nonGroupedActions.map((action) => {
      const formattedName: string = KMainFunctions.getFormattedSystemActionName(action);
      result.push({
        category: EActionNodeCategory.action,
        label: this.getActionOptionLabel(action, formattedName),
        searchLabel: formattedName,
        value: action.id,
        children: undefined
      });
    });

    // Sort result
    return result.sort((a, b) => {
      // Put subcategories first
      if (a.category === EActionNodeCategory.actionCategory && b.category !== EActionNodeCategory.actionCategory) {
        return -1;
      }
      if (a.category !== EActionNodeCategory.actionCategory && b.category === EActionNodeCategory.actionCategory) {
        return 1;
      }

      // Then sort by name (searchLabel-aware — labels may be elements)
      return this.getStringValueForSort(a).localeCompare(this.getStringValueForSort(b), undefined, {
        sensitivity: "base"
      });
    });
  };

  generateDataSources = (): CascaderOptionType[] => {
    const curDashboard = this.props.sd.dashboardState;
    const result: CascaderOptionType[] = [];
    const isCurrentOfflineScreen: boolean = this.props.sd.project.offlineScreenId === KMainFunctions.getSdIdFromUrl();

    const existingDatasourceIds = new Set<string>();

    const shouldSkipDatasource = (ad: IActionDefinitionDto): boolean => {
      if (ad.kDatasource_Name === "SystemJsFunctions") return true;
      if (existingDatasourceIds.has(ad.kDatasource_Id)) return true;
      if (ad.actionCategory === EActionCategory.ManagedDb) return true;
      if (isCurrentOfflineScreen && !isOfflineCompatibleActionDefinition(ad)) return true;

      return (
        (curDashboard === DashboardState.scheduledJobs ||
          curDashboard === DashboardState.apiManagement ||
          curDashboard === DashboardState.workFlow) &&
        (ad.actionType === EDatasourceType.Blockly ||
          ad.actionType === EDatasourceType.JavaScriptAction ||
          ad.actionType === EDatasourceType.PythonAction ||
          ad.actionType === EDatasourceType.Mqtt ||
          ad.actionType === EDatasourceType.BleService)
      );
    };

    const datasourceMap = new Map<string, IActionDatasourceOption>();

    for (const ad of this.props.sd.actionDefinitions || []) {
      if (shouldSkipDatasource(ad)) continue;

      existingDatasourceIds.add(ad.kDatasource_Id);

      // The same label a same-named action's card is qualified with (KOA-18147), so the two never drift.
      const label: string = getDatasourceLabel(ad.actionType, ad.kDatasource_Name);
      if (label && label !== "") {
        datasourceMap.set(ad.kDatasource_Id, {
          label,
          value: ad.kDatasource_Id
        });
      }
    }

    for (const [datasourceId, { label, value }] of datasourceMap.entries()) {
      const children = this.generateActionsByDataSource(datasourceId);

      // The map is built from every action definition, but the children are filtered — a datasource with nothing
      // offerable in the current context would otherwise show up as an empty folder to click into.
      if (!children || children.length === 0) continue;

      result.push({
        category: EActionNodeCategory.actionCategory,
        label,
        value,
        children
      });
    }

    return result;
  };

  generatePayments = (): CascaderOptionType[] => {
    const result: CascaderOptionType[] = [];
    const item: CascaderOptionType = {
      category: EActionCategory.Payment,
      label: "Stripe",
      value: "Stripe",
      children: this.generateStripe()
    };
    const iyzico: CascaderOptionType = {
      category: EActionCategory.Payment,
      label: "Iyzico",
      value: "Iyzico",
      children: this.generateIyzico()
    };
    const paramPayment: CascaderOptionType = {
      category: EActionCategory.Payment,
      label: "Param",
      value: "Param",
      children: this.generateParamPayment()
    };
    if (this.props.sd.project.offlineScreenId === KMainFunctions.getSdIdFromUrl()) {
      return result;
    }
    if (item.label && item.label !== "") {
      result.push(item);
    }
    if (iyzico.label && iyzico.label !== "") {
      result.push(iyzico);
    }
    if (paramPayment.label && paramPayment.label !== "") {
      result.push(paramPayment);
    }
    return result;
  };

  generateStripe = (): CascaderOptionType[] => {
    let actionDefinitions = _.clone(this.props.sd.actionDefinitions);

    if (
      this.props.sd.dashboardState === DashboardState.scheduledJobs ||
      this.props.sd.dashboardState === DashboardState.workFlow ||
      this.props.sd.dashboardState === DashboardState.apiManagement
    ) {
      actionDefinitions = actionDefinitions.filter((x) => {
        return (
          x.actionType != EDatasourceType.SystemJSFunctions &&
          x.actionType != EDatasourceType.CustomAuthorizationProvider &&
          x.actionType != EDatasourceType.CustomAuthenticationProvider &&
          x.actionType != EDatasourceType.CustomVerificationCodeSenderProvider &&
          x.actionType != EDatasourceType.CustomConnectionStringProvider &&
          x.actionType != EDatasourceType.CustomExternalTokenValidatorProvider &&
          x.actionType != EDatasourceType.CustomMailProvider
        );
      });
    }

    if (
      this.props.sd.dashboardState === DashboardState.dataDiagramScheme &&
      this.props.sd.getSelectedBluetoothMethod()
    ) {
      actionDefinitions = actionDefinitions.filter((x) => {
        return x.actionType === EDatasourceType.SystemJSFunctions;
      });
    }

    // Payment actions return ahead of getActionDefinitionsByType's platform filter, so without this a Mobile
    // project would still be offered whatever the catalog marks Web-only. Apply the same gate here.
    // (RNM-0123 cleared the flag on UpdateStripeUserInfo — the React Native target implements it — so no payment
    // action is Web-only today. The gate stays: it is the mechanism, not a workaround for one action.)
    const platform: AttachablePlatformContext = this.getAttachablePlatformContext();
    actionDefinitions = actionDefinitions.filter((x) => isActionDefinitionPlatformCompatible(x, platform));

    const tmp = actionDefinitions
      ?.filter((x) => x.actionCategory == EActionCategory.Payment)
      ?.filter((x) => !x.name.includes("Iyzico") && !x.name.includes("ParamPayment"))
      ?.map((x) => {
        const formattedName: string = KMainFunctions.getFormattedSystemActionName(x);
        return {
          category: EActionNodeCategory.action,
          label: this.getActionOptionLabel(x, formattedName),
          searchLabel: formattedName,
          value: x.id,
          children: undefined
        } as CascaderOptionType;
      })
      .sort((n1: CascaderOptionType, n2: CascaderOptionType) => {
        const label1 = this.getStringValueForSort(n1);
        const label2 = this.getStringValueForSort(n2);
        return label1.localeCompare(label2, undefined, { sensitivity: "base" });
      });
    return tmp;
  };

  generateIyzico = (): CascaderOptionType[] => {
    let actionDefinitions = _.clone(this.props.sd.actionDefinitions);

    if (
      this.props.sd.dashboardState === DashboardState.scheduledJobs ||
      this.props.sd.dashboardState === DashboardState.workFlow ||
      this.props.sd.dashboardState === DashboardState.apiManagement
    ) {
      actionDefinitions = actionDefinitions.filter((x) => {
        return (
          x.actionType != EDatasourceType.SystemJSFunctions &&
          x.actionType != EDatasourceType.CustomAuthorizationProvider &&
          x.actionType != EDatasourceType.CustomAuthenticationProvider &&
          x.actionType != EDatasourceType.CustomVerificationCodeSenderProvider &&
          x.actionType != EDatasourceType.CustomConnectionStringProvider &&
          x.actionType != EDatasourceType.CustomExternalTokenValidatorProvider &&
          x.actionType != EDatasourceType.CustomMailProvider
        );
      });
    }

    if (
      this.props.sd.dashboardState === DashboardState.dataDiagramScheme &&
      this.props.sd.getSelectedBluetoothMethod()
    ) {
      actionDefinitions = actionDefinitions.filter((x) => {
        return x.actionType === EDatasourceType.SystemJSFunctions;
      });
    }

    const tmp = actionDefinitions
      ?.filter((x) => x.actionCategory == EActionCategory.Payment)
      ?.filter((x) => x.name.includes("Iyzico"))
      ?.map((x) => {
        const formattedName: string = KMainFunctions.getFormattedSystemActionName(x);
        return {
          category: EActionNodeCategory.action,
          label: this.getActionOptionLabel(x, formattedName),
          searchLabel: formattedName,
          value: x.id,
          children: undefined
        } as CascaderOptionType;
      })
      .sort((n1: CascaderOptionType, n2: CascaderOptionType) => {
        const label1 = this.getStringValueForSort(n1);
        const label2 = this.getStringValueForSort(n2);
        return label1.localeCompare(label2, undefined, { sensitivity: "base" });
      });
    return tmp;
  };

  generateParamPayment = (): CascaderOptionType[] => {
    let actionDefinitions = _.clone(this.props.sd.actionDefinitions);

    if (
      this.props.sd.dashboardState === DashboardState.scheduledJobs ||
      this.props.sd.dashboardState === DashboardState.workFlow ||
      this.props.sd.dashboardState === DashboardState.apiManagement
    ) {
      actionDefinitions = actionDefinitions.filter((x) => {
        return (
          x.actionType != EDatasourceType.SystemJSFunctions &&
          x.actionType != EDatasourceType.CustomAuthorizationProvider &&
          x.actionType != EDatasourceType.CustomAuthenticationProvider &&
          x.actionType != EDatasourceType.CustomVerificationCodeSenderProvider &&
          x.actionType != EDatasourceType.CustomConnectionStringProvider &&
          x.actionType != EDatasourceType.CustomExternalTokenValidatorProvider &&
          x.actionType != EDatasourceType.CustomMailProvider
        );
      });
    }

    if (
      this.props.sd.dashboardState === DashboardState.dataDiagramScheme &&
      this.props.sd.getSelectedBluetoothMethod()
    ) {
      actionDefinitions = actionDefinitions.filter((x) => {
        return x.actionType === EDatasourceType.SystemJSFunctions;
      });
    }

    const tmp = actionDefinitions
      ?.filter((x) => x.actionCategory == EActionCategory.Payment)
      ?.filter((x) => x.name.includes("ParamPayment"))
      ?.map((x) => {
        const formattedName: string = KMainFunctions.getFormattedSystemActionName(x);
        return {
          category: EActionNodeCategory.action,
          label: this.getActionOptionLabel(x, formattedName),
          searchLabel: formattedName,
          value: x.id,
          children: undefined
        } as CascaderOptionType;
      })
      .sort((n1: CascaderOptionType, n2: CascaderOptionType) => {
        const label1 = this.getStringValueForSort(n1);
        const label2 = this.getStringValueForSort(n2);
        return label1.localeCompare(label2, undefined, { sensitivity: "base" });
      });
    return tmp;
  };

  getDatasourceIcon = (dbId: string, dbName: string) => {
    const datasource: IDatasourceDto | undefined = this.props.sd.datasources.find((ds) => ds.id === dbId);
    if (datasource?.connectionString === "") {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <svg width="18" height="18" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M7.79816 1C8.47307 1 9 1.54199 9 2.1992V7.8008C9 8.47439 8.47307 9 7.79816 9H2.20146C1.52674 9 1 8.45801 1 7.8008V2.1992C1 1.52561 1.54293 1 2.20146 1H7.79816Z"
              stroke="var(--dark-6)"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M4.61838 2.48572C4.46753 2.48572 4.30157 2.53423 4.1251 2.63015C3.31059 3.07216 2.70501 3.74412 2.4096 4.22573C2.26124 4.46788 2.1685 4.65641 2.14844 4.86954L2.14312 4.97055L2.14288 5.02296L2.14461 5.0659C2.15051 5.30661 2.2464 5.50792 2.4096 5.77447C2.70501 6.25589 3.31078 6.92803 4.1251 7.37005C4.30139 7.46578 4.46753 7.51429 4.61819 7.51429C4.72994 7.51429 4.8142 7.48794 4.87463 7.463L4.93592 7.43548C5.10155 7.35836 5.21819 7.25052 5.28301 7.11454L5.30269 7.06818L5.31996 7.02388C5.34132 6.96381 5.36447 6.85832 5.30362 6.71334C5.25571 6.59913 5.1372 6.47345 5.04186 6.38125L4.91619 6.2624L4.7739 6.11771C4.46923 5.80257 4.15475 5.42676 4.16032 5.00103C4.15416 4.52853 4.53898 4.11887 4.8694 3.78482L4.93019 3.72402L5.04177 3.61883C5.1372 3.52667 5.25571 3.40088 5.30362 3.28667C5.36447 3.14168 5.34132 3.0362 5.31996 2.97613L5.30269 2.93201C5.24925 2.78948 5.14398 2.6751 4.98904 2.59132L4.92725 2.56063L4.87471 2.53708C4.81432 2.51207 4.72999 2.48572 4.61838 2.48572Z"
              fill="var(--dark-6)"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6.25694 3.17145C5.69256 3.17145 5.06298 3.95787 4.88545 4.19588L4.82985 4.27358C4.77701 4.35726 4.46457 4.90195 4.83678 5.54169C5.03545 5.88317 5.58469 6.46439 6.20713 6.59623L6.22972 6.59915L6.26393 6.5999C6.84677 6.59394 7.49645 5.78275 7.64579 5.5856L7.68419 5.53243C7.73688 5.44805 8.04931 4.89892 7.67693 4.25899C7.44749 3.86501 6.89486 3.25117 6.31147 3.17454C6.29393 3.17222 6.27581 3.17145 6.25694 3.17145Z"
              fill="var(--dark-6)"
            />
          </svg>
          <span>{dbName}</span>
        </div>
      );
    }
    if (
      datasource?.datasourceType === EDatasourceType.CustomDb ||
      datasource?.datasourceType === EDatasourceType.Oracle ||
      datasource?.datasourceType === EDatasourceType.Postgre ||
      datasource?.datasourceType === EDatasourceType.MsSql
    ) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <svg width="17" height="18" viewBox="0 0 9 10" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M4.40426 1C2.52374 1 1 1.68545 1 2.53192C1 3.37838 2.52374 4.06383 4.40426 4.06383C6.28477 4.06383 7.80851 3.37838 7.80851 2.53192C7.80851 1.68545 6.28477 1 4.40426 1Z"
              stroke="var(--dark-6)"
              strokeWidth="0.8"
              strokeMiterlimit="10"
              strokeLinejoin="round"
            />
            <path
              d="M1 2.53192C1 2.88494 1 7.11507 1 7.46809C1 8.31456 2.52374 9.00001 4.40426 9.00001C6.28477 9.00001 7.80851 8.31456 7.80851 7.46809C7.80851 7.11507 7.80851 2.88494 7.80851 2.53192"
              stroke="var(--dark-6)"
              strokeWidth="0.8"
              strokeMiterlimit="10"
              strokeLinejoin="round"
            />
            <path
              d="M1 5.9362C1 6.78267 2.52374 7.46811 4.40426 7.46811C6.28477 7.46811 7.80851 6.78267 7.80851 5.9362M1 4.23407C1 5.08054 2.52374 5.76598 4.40426 5.76598C6.28477 5.76598 7.80851 5.08054 7.80851 4.23407"
              stroke="var(--dark-6)"
              strokeWidth="0.8"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M4.40426 1C2.52374 1 1 1.68545 1 2.53192C1 3.37838 2.52374 4.06383 4.40426 4.06383C6.28477 4.06383 7.80851 3.37838 7.80851 2.53192C7.80851 1.68545 6.28477 1 4.40426 1Z"
              stroke="var(--dark-6)"
              strokeWidth="0.8"
              strokeMiterlimit="10"
              strokeLinejoin="round"
            />
            <path
              d="M1 2.53192C1 2.88494 1 7.11507 1 7.46809C1 8.31456 2.52374 9.00001 4.40426 9.00001C6.28477 9.00001 7.80851 8.31456 7.80851 7.46809C7.80851 7.11507 7.80851 2.88494 7.80851 2.53192"
              stroke="var(--dark-6)"
              strokeWidth="0.8"
              strokeMiterlimit="10"
              strokeLinejoin="round"
            />
            <path
              d="M1 5.9362C1 6.78267 2.52374 7.46811 4.40426 7.46811C6.28477 7.46811 7.80851 6.78267 7.80851 5.9362M1 4.23407C1 5.08054 2.52374 5.76598 4.40426 5.76598C6.28477 5.76598 7.80851 5.08054 7.80851 4.23407"
              stroke="var(--dark-6)"
              strokeWidth="0.8"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>{dbName}</span>
        </div>
      );
    }
    if (datasource?.datasourceType === EDatasourceType.RestApi) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <svg width="18" height="16" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M11.7027 11.0541H2.2973C1.58086 11.0541 1 10.4732 1 9.75676V2.2973C1 1.58086 1.58086 1 2.2973 1H11.7027C12.4191 1 13 1.58086 13 2.2973V9.75676C13 10.4732 12.4191 11.0541 11.7027 11.0541Z"
              stroke="var(--dark-6)"
              strokeWidth="1.4"
              strokeMiterlimit="10"
            />
            <path
              d="M5.99885 7.41868L4.78263 4.3376C4.72133 4.18257 4.57182 4.08105 4.40544 4.08105C4.23906 4.08105 4.08955 4.18257 4.02825 4.3376L2.81203 7.41868C2.72998 7.62689 2.83214 7.86235 3.04036 7.94473C3.24922 8.02711 3.48403 7.92462 3.56641 7.71641L3.68933 7.40538H5.1222L5.24512 7.71641C5.30739 7.87597 5.46014 7.97295 5.62198 7.97295C5.67128 7.97295 5.72187 7.96387 5.77052 7.94441C5.97874 7.86235 6.0809 7.62689 5.99885 7.41868ZM4.00912 6.59457L4.40544 5.59046L4.80176 6.59457H4.00912Z"
              fill="var(--dark-6)"
            />
            <path
              d="M10.6486 7.97295C10.4248 7.97295 10.2432 7.79132 10.2432 7.56754V4.48646C10.2432 4.26268 10.4248 4.08105 10.6486 4.08105C10.8724 4.08105 11.054 4.26268 11.054 4.48646V7.56754C11.054 7.79132 10.8724 7.97295 10.6486 7.97295Z"
              fill="var(--dark-6)"
            />
            <path
              d="M8.13512 4.08105H7.08106C6.85728 4.08105 6.67566 4.26268 6.67566 4.48646V7.56754C6.67566 7.79132 6.85728 7.97295 7.08106 7.97295C7.30485 7.97295 7.48647 7.79132 7.48647 7.56754V6.99997H8.13512C8.93977 6.99997 9.59458 6.34516 9.59458 5.54051C9.59458 4.73587 8.93977 4.08105 8.13512 4.08105ZM8.13512 6.18916H7.48647V4.89187H8.13512C8.49285 4.89187 8.78377 5.18278 8.78377 5.54051C8.78377 5.89824 8.49285 6.18916 8.13512 6.18916Z"
              fill="var(--dark-6)"
            />
          </svg>
          <span>{dbName}</span>
        </div>
      );
    }
    if (
      datasource?.datasourceType === EDatasourceType.CSharpFunctions ||
      datasource?.datasourceType === EDatasourceType.CustomAuthenticationProvider ||
      datasource?.datasourceType === EDatasourceType.CustomAuthorizationProvider ||
      datasource?.datasourceType === EDatasourceType.CustomVerificationCodeSenderProvider ||
      datasource?.datasourceType === EDatasourceType.CustomConnectionStringProvider ||
      datasource?.datasourceType === EDatasourceType.CustomExternalTokenValidatorProvider ||
      datasource?.datasourceType === EDatasourceType.CustomMailProvider
    ) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <svg width="18" height="18" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g opacity="0.8">
              <path
                d="M8.0303 1H1.9697C1.43418 1 1 1.43418 1 1.9697V8.0303C1 8.56582 1.43418 9 1.9697 9H8.0303C8.56582 9 9 8.56582 9 8.0303V1.9697C9 1.43418 8.56582 1 8.0303 1Z"
                stroke="var(--dark-6)"
                strokeMiterlimit="10"
              />
              <path d="M2.93939 4.15155H7.0606" stroke="var(--dark-6)" strokeMiterlimit="10" strokeLinecap="round" />
              <path d="M2.93939 5.84845H7.0606" stroke="var(--dark-6)" strokeMiterlimit="10" strokeLinecap="round" />
              <path d="M5.84845 2.93939V7.0606" stroke="var(--dark-6)" strokeMiterlimit="10" strokeLinecap="round" />
              <path d="M4.15155 2.93939V7.0606" stroke="var(--dark-6)" strokeMiterlimit="10" strokeLinecap="round" />
            </g>
          </svg>
          <span>{dbName}</span>
        </div>
      );
    }
  };

  generateActionsByDataSource = (datasourceId: string): CascaderOptionType[] => {
    const result: CascaderOptionType[] = [];
    const actionDefinitionsMap = new Map();
    const isCurrentOfflineScreen: boolean = this.props.sd.project.offlineScreenId === KMainFunctions.getSdIdFromUrl();

    const isDocumentParametersContext: boolean = this.props.sd.isDocumentParameterAuthoringActive();

    this.props.sd.actionDefinitions?.forEach((ad) => {
      if (ad.kDatasource_Id === datasourceId) {
        if (isCurrentOfflineScreen && !isOfflineCompatibleActionDefinition(ad)) {
          return;
        }

        // Datasource actions reach the cascader through this path rather than the category filter, so the
        // Document Parameters restriction is applied here as well: a database query returning a row, nothing else.
        if (isDocumentParametersContext && !isDocumentParameterSourceAction(ad)) {
          return;
        }

        actionDefinitionsMap.set(ad.id, ad);
      }
    });

    const actionIdsInFolders = new Set();
    this.props.sd.project.actionFolders?.forEach((folder) => {
      folder.actionIds?.forEach((id) => actionIdsInFolders.add(id));

      // Process nested children recursively
      const addNestedActionIds = (f) => {
        f.actionIds?.forEach((id) => actionIdsInFolders.add(id));
        f.children?.forEach(addNestedActionIds);
      };

      folder.children?.forEach(addNestedActionIds);
    });

    for (const [id, ad] of actionDefinitionsMap.entries()) {
      if (!actionIdsInFolders.has(id)) {
        if (
          this.props.sd.dashboardState === DashboardState.design ||
          !(ad.actionType === EDatasourceType.Mqtt && ad.actionTemplate.includes("subscribe"))
        ) {
          result.push({
            category: EActionNodeCategory.action,
            label: this.getActionOptionLabel(ad, ad.name),
            searchLabel: ad.name,
            value: ad.id,
            children: []
          });
        }
      }
    }

    const mapFolderToCascaderOption = (folder): CascaderOptionType => {
      const children = folder.children?.map((childFolder) => mapFolderToCascaderOption(childFolder)) || [];

      const actions: CascaderOptionType[] = [];
      folder.actionIds?.forEach((id) => {
        const ad = actionDefinitionsMap.get(id);
        if (ad?.name !== undefined) {
          actions.push({
            category: EActionNodeCategory.action,
            label: this.getActionOptionLabel(ad, ad.name),
            searchLabel: ad.name,
            value: id
          });
        }
      });

      // Use a Set to prevent duplicates - O(n) instead of O(n²).
      // Keyed by the plain name (labels may be elements).
      const uniqueLabels = new Set<string>();
      const combinedChildren: CascaderOptionType[] = [];

      // Process actions first
      for (const action of actions) {
        const actionName: string = this.getStringValueForSort(action);
        if (actionName !== "" && !uniqueLabels.has(actionName)) {
          uniqueLabels.add(actionName);
          combinedChildren.push(action);
        }
      }

      // Then process children
      for (const child of children) {
        const childName: string = this.getStringValueForSort(child);
        if (childName !== "" && !uniqueLabels.has(childName)) {
          uniqueLabels.add(childName);
          combinedChildren.push(child);
        }
      }

      const folderItem: CascaderOptionType = {
        category: EActionNodeCategory.actionFolder,
        label: folder.name,
        value: folder.id,
        children: combinedChildren
      };

      return folderItem;
    };

    this.props.sd.project.actionFolders
      ?.filter((x) => x.dataSourceType === datasourceId)
      ?.forEach((af) => {
        result.push(mapFolderToCascaderOption(af));
      });

    return result.sort((n1, n2) => {
      if (n1.category === EActionNodeCategory.actionFolder && n2.category !== EActionNodeCategory.actionFolder) {
        return -1;
      }
      if (n1.category !== EActionNodeCategory.actionFolder && n2.category === EActionNodeCategory.actionFolder) {
        return 1;
      }

      return this.getStringValueForSort(n1).toLowerCase().localeCompare(this.getStringValueForSort(n2).toLowerCase());
    });
  };

  getCategoryIconName = (category: string): string => getActionCategoryIconName(Number(category) as EActionCategory);

  getCategoryDisplayName = (category: string): string => {
    if (category === EActionCategory.DocumentManagement.toString()) {
      return "Document Management";
    }
    if (category === EActionCategory.SearchEngine.toString()) {
      return "Full-Text Search";
    }
    if (category === EActionCategory.VectorSearch.toString()) {
      return "Vector Search (Qdrant)";
    }
    return EActionCategory[category];
  };

  // KP Actions Panel redesign: icon + name (+ BETA badge) + muted action count.
  // Element labels are already first-class here — sort/search fall back to the
  // option value, exactly as the previous BETA-badge labels did.
  isBetaCategory = (category: string): boolean =>
    category === EActionCategory.EAV.toString() ||
    category === EActionCategory.DocumentManagement.toString() ||
    category === EActionCategory.SearchEngine.toString();

  /** What the popup draws a category row from — its label is an element it cannot read back. */
  getCategoryPickerMeta = (category: string): IActionPickerOptionMeta => ({
    label: this.getCategoryDisplayName(category),
    iconName: this.getCategoryIconName(category),
    isBeta: this.isBetaCategory(category),
    isCustom: category === EActionCategory.Custom.toString()
  });

  getCategoryLabel = (category: string, actionCount?: number) => {
    const isBeta: boolean = this.isBetaCategory(category);
    const isCustom: boolean = category === EActionCategory.Custom.toString();

    return (
      <span className={`kuika-action-category-option${isCustom ? " kuika-action-category-option--custom" : ""}`}>
        <KuikaSvg
          className="kuika-action-category-option__icon"
          name={this.getCategoryIconName(category)}
          width={16}
          height={16}
        />
        <MiddleTruncate className="kuika-action-category-option__name" text={this.getCategoryDisplayName(category)} />
        {isBeta && <KuikaBadge content="BETA" />}
        {actionCount !== undefined && actionCount > 0 && (
          <span className="kuika-action-category-option__count">({formatCategoryCount(actionCount)})</span>
        )}
      </span>
    );
  };

  // Action rows carry a return-type glyph with a tooltip naming the exact type (KP Actions Panel
  // design). Both shapes truncate their name in the MIDDLE (KOA-18357): the columns are now
  // bounded, and an action's tail ("...ByCustomerId") is what tells two siblings apart. Sorting
  // and search are unaffected — every call site stores the plain name in `searchLabel`, which
  // getStringValueForSort and showSearch.render read instead of the label node.
  getActionOptionLabel = (ad: IActionDefinitionDto, formattedName: string): React.ReactNode => {
    const returnTypeName: string = ad.returnTypeName ?? "";
    if (isVoidReturnType(returnTypeName)) {
      return <MiddleTruncate text={formattedName} />;
    }
    const mappedIcon: string | undefined = getActionReturnTypeIconName(returnTypeName);
    return (
      <span className="kuika-action-option">
        <MiddleTruncate className="kuika-action-option__name" text={formattedName} />
        <KuikaTooltip content={returnTypeName} position="top">
          <span className="kuika-action-option__return-type">
            {mappedIcon ? (
              <KuikaSvg name={mappedIcon} type="action-icons" width={14} height={14} />
            ) : (
              <KuikaSvg name="info-circle" type="common" width={14} height={14} />
            )}
          </span>
        </KuikaTooltip>
      </span>
    );
  };

  countLeafActions = (options: CascaderOptionType[]): number => {
    let count: number = 0;
    for (const option of options) {
      if (option.children && option.children.length > 0) {
        count += this.countLeafActions(option.children);
      } else {
        count += 1;
      }
    }
    return count;
  };

  generateActionDefinitionItems = (isInitialAction?: boolean): IActionPickerOption[] => {
    const result: IActionPickerOption[] = [];
    const hasAccessManageCustomAction = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.ManageCustomActions,
      this.props.sd.project?.projectAccess
    );
    const hasAccessEditSystemAction = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.AddEditSystemActions,
      this.props.sd.project?.projectAccess
    );
    if (!hasAccessManageCustomAction && !hasAccessEditSystemAction) {
      return result;
    }

    for (const value in EActionCategory) {
      if (isNaN(Number(value))) {
        continue;
      }
      if (this.props.sd.dashboardState === DashboardState.reportDesigner) {
        if (EActionCategory[value] === "Payment") {
          continue;
        }
      }
      if (
        KMainFunctions.isMainAI() &&
        (EActionCategory[value] === "VectorSearch" || EActionCategory[value] === "OfflineDatabase")
      ) {
        continue;
      }
      const children: CascaderOptionType[] = this.getActionDefinitionsByType(
        Number(value) as EActionCategory,
        isInitialAction
      );
      if (!children || children.length === 0) {
        continue;
      }
      const item: IActionPickerOption = {
        category: value,
        label: this.getCategoryLabel(value, this.countLeafActions(children)),
        pickerMeta: this.getCategoryPickerMeta(value),
        value: EActionCategory[value],
        children
      };
      result.push(item);
    }

    return result.sort((n1: CascaderOptionType, n2: CascaderOptionType) => {
      const getStringValue = (item: CascaderOptionType) => {
        if (!item.label) return "";
        if (typeof item.label === "string") return item.label;
        // If label is a React element, use the value instead
        return item.value ? String(item.value) : "";
      };

      const label1 = getStringValue(n1);
      const label2 = getStringValue(n2);

      return label1.localeCompare(label2, undefined, { sensitivity: "base" });
    });
  };

  handleAddAction = (value: CascaderValueType, selectedOptions?: CascaderOptionType[] | undefined) => {
    // Check if adding actions is allowed
    if (this.props.preventAddAction === true) {
      return;
    }

    if (!selectedOptions || selectedOptions.length == 0) {
      return;
    }
    let eventName = "";
    selectedOptions.forEach((node) => {
      switch (node.category) {
        case EActionNodeCategory.event: {
          eventName = node.value!.toString();
          break;
        }
        case EActionNodeCategory.triggerEventAction: {
          const actions = _.clone(this.props.sd.actionDefinitions);
          const actionDef = actions.find((p) => p.name === "TriggerEvent");
          if (actionDef && selectedOptions.length > 3) {
            const targetDiNode = selectedOptions[2];
            const targetEventNode = selectedOptions[3];
            const description = `Trigger [diId:${targetDiNode.value}] > ${targetEventNode.label}`;

            const targetDiIdActionParam = actionDef.actionParameters.find((x) => x.name === "targetDiId");
            const targetEventNameActionParam = actionDef.actionParameters.find((x) => x.name === "targetEventName");

            if (targetDiIdActionParam && targetEventNameActionParam) {
              targetDiIdActionParam.symbol = {
                category: ESymbolCategory.fixed,
                fixedValue: targetDiNode.value
              } as ISymbol;

              targetEventNameActionParam.symbol = {
                category: ESymbolCategory.fixed,
                fixedValue: targetEventNode.value
              } as ISymbol;

              const actionParams = [targetDiIdActionParam, targetEventNameActionParam];
              const action = {
                id: Guid.create().toString(),
                actionDefinitionId: actionDef.id,
                label: actionDef.name,
                description,
                parameters: actionParams,
                returnType: actionDef.returnTypeName,
                returnCardinality: actionDef.returnCardinality
              } as IAction;
              this.createActionByEventName(action, eventName);
            }
          }
          break;
        }
        case EActionNodeCategory.action:
          const actions = _.clone(this.props.sd.actionDefinitions);
          let actionDef = actions.find((p) => p.id === node.value);
          if (node.value === "Recurring") {
            actionDef = actions.find((p) => p.id.toUpperCase() === "58A84621-9C6F-4AA0-80A3-6ED45947F192");
          }
          if (actionDef) {
            if (actionDef.actionCategory === EActionCategory.ProcessAutomation) {
              const accessName = "Workflow";
              if (KMainFunctions.isDomainHasAccess(accessName) === false) {
                this.props.showDomainAccessRequiredModal(accessName);
                return;
              }
            }
            const actionId = Guid.create();
            const action = {
              id: actionId.toString(),
              actionDefinitionId: actionDef.id,
              label: actionDef.name,
              description: actionDef.name,
              parameters: actionDef.actionParameters.map((x) => this.cloneActionParameter(x)),
              returnType: actionDef.returnTypeName,
              returnCardinality: actionDef.returnCardinality
            } as IAction;
            this.createActionByEventName(action, eventName);
          }
          break;
      }
    });
    // this.bottomRef?.scrollIntoView({ behavior: "smooth" });
  };

  cloneActionParameter = (actionParameter: IActionParameter) => {
    const tmp = _.cloneDeep(actionParameter);
    if (tmp.name === "executePageInitActionsAfterClose" && !tmp.symbol) {
      tmp.symbol = {
        category: ESymbolCategory.fixed,
        fixedValue: true
      } as ISymbol;
      tmp.symbol.fixedValue = true;
    }
    return tmp;
  };

  getSelectedKuikaIds = () => {
    if (
      this.props.sd.dashboardState === DashboardState.dataDiagramScheme ||
      this.props.sd.dashboardState === DashboardState.workFlow
    ) {
      return ["-1"];
    }
    return this.props.sd.selectedKuikaIds;
  };

  createActionByEventName = (action: IAction, eventName: string | number | undefined, isPaste: boolean = false) => {
    const sJob = this.props.sd.getSelectedSj();
    const api = this.props.sd.getSelectedApi();
    const apiMethod = this.props.sd.getSelectedApiMethod();
    const wf = this.props.sd.selectedWf;
    const wfStep = this.props.sd.getSelectedWfStep();
    const ds = this.props.sd.activeEditingDatasource;
    const bleMethod = this.props.sd.getSelectedBluetoothMethod();

    const { dashboardState } = this.props.sd;

    if (sJob && dashboardState === DashboardState.scheduledJobs) {
      eventName = "scheduledJobInit";
    } else if (sJob === undefined && dashboardState === DashboardState.scheduledJobs) {
      return;
    } else if (this.props.sd.isDocumentParameterAuthoringActive()) {
      eventName = "documentParametersInit";
    } else if (api && apiMethod && dashboardState === DashboardState.apiManagement) {
      eventName = "apiMethodInit";
    } else if ((api === undefined || apiMethod === undefined) && dashboardState === DashboardState.apiManagement) {
      return;
    } else if (wf && wfStep && dashboardState === DashboardState.workFlow) {
      eventName = "workFlowStepInit";
    } else if ((wf === undefined || wfStep === undefined) && dashboardState === DashboardState.workFlow) {
      return;
    } else if (
      ds?.datasourceType === EDatasourceType.BleService &&
      bleMethod &&
      this.props.sd.dashboardState == DashboardState.dataDiagramScheme
    ) {
      eventName = "bluetoothMethodInit";
    } else if (
      (ds?.datasourceType !== EDatasourceType.BleService || !bleMethod) &&
      this.props.sd.dashboardState === DashboardState.dataDiagramScheme
    ) {
      return;
    }

    if (
      action.description == "RecurringActions" ||
      action.actionDefinitionId?.toUpperCase() === "58A84621-9C6F-4AA0-80A3-6ED45947F192"
    ) {
      eventName = "Recurring";
    }

    const diEvents: CascaderOptionType[] = this.getEventOptions();
    let targetEventFromDi: CascaderOptionType | undefined = diEvents.find((o) => o.value === eventName);

    if (!targetEventFromDi && isPaste) {
      targetEventFromDi = diEvents[0];
      if (targetEventFromDi?.value) eventName = targetEventFromDi.value;
      else eventName = "pageInit";
    }

    if (
      (this.getSelectedKuikaIds()[0] === "-1" && eventName === "pageInit") ||
      (this.getSelectedKuikaIds()[0] === "-1" && eventName === "Recurring") ||
      eventName === "apiMethodInit" ||
      eventName === "scheduledJobInit" ||
      eventName === "workFlowStepInit" ||
      eventName === "bluetoothMethodInit" ||
      eventName === "documentParametersInit"
    ) {
      targetEventFromDi = {};
    }

    if (!targetEventFromDi) {
      return;
    }

    if (action.description === "RecurringActions" && eventName !== "Recurring") {
      KMainFunctions.displayWarningNotification("RecurringActions can only be pasted to Recurring event");
      return;
    }

    const kuikaId = this.getSelectedKuikaIds()[0];
    const event: IEvent = { actions: [], eventName } as IEvent;

    action.parameters.forEach((p) => {
      const prm = action.parameters?.find((up) => up.name === p.name);
      if (prm !== undefined && prm.symbol === undefined) {
        prm.symbol = KMainFunctions.getDefaultSymbol(prm.Typename);
      }
    });

    LocalStorageService.setExpandedPanelItem(action.id, true);
    this.props.sd.saveActionToEvent(kuikaId, action, event);
    this.setFocusToFirstInput(action.id);
  };

  setFocusToFirstInput = (actionId: string) => {
    // Limit the number of retries to avoid infinite loops
    let retryCount = 0;
    const maxRetries = 10;

    const tryFocus = () => {
      if (retryCount >= maxRetries) return;

      const el = document.querySelector(`[data-rbd-drag-handle-draggable-id="draggable_${actionId}"]`);
      if (!el) {
        retryCount++;
        setTimeout(tryFocus, 100);
        return;
      }

      focusFirstEditableControl(el);
    };

    tryFocus();
  };

  getSelectedDisplayItem = () => {
    const sd = this.props.sd.getScreenDesign();
    const selectedDisplayItem = this.props.sd.getSelectedDisplayItem(sd);
    return selectedDisplayItem;
  };

  reorderEventActions = (result: any, sourceIdx: number, destIdx: number) => {
    const di = this.getSelectedDisplayItem();
    if (!di) return;
    const component: IDisplayItemComponent = di.item;
    const allEvents = component.events;
    if (result.source.droppableId.substring(10) == result.destination.droppableId.substring(10)) {
      const eventName = result.source.droppableId.substring(10);

      const eventsToEdit = allEvents?.filter((x) => x.eventName === eventName); // TODO
      if (eventsToEdit !== undefined && eventsToEdit.length > 0) {
        const eventToEdit = eventsToEdit[0];
        const result = Array.from(eventToEdit.actions);
        const [removed] = result.splice(sourceIdx, 1);
        result.splice(destIdx, 0, removed);
        eventToEdit.actions = result;
      }
    } else {
      const sourceName = result.destination.droppableId.substring(10);
      const destinationName = result.source.droppableId.substring(10);

      const sourcesToEdit = allEvents?.filter((x) => x.eventName === sourceName);
      const destinationsToEdit = allEvents?.filter((x) => x.eventName === destinationName);

      if (
        sourcesToEdit !== undefined &&
        sourcesToEdit.length > 0 &&
        destinationsToEdit !== undefined &&
        destinationsToEdit.length > 0
      ) {
        const sourceToEdit = sourcesToEdit[0];
        const destinationToEdit = destinationsToEdit[0];

        const result1 = Array.from(sourceToEdit.actions);
        const result2 = Array.from(destinationToEdit.actions);

        const [removed] = result2.splice(sourceIdx, 1);
        result1.splice(destIdx, 0, removed);
        sourceToEdit.actions = result1;
        destinationToEdit.actions = result2;
      }
    }
    // KOA-18167: a bare forceUpdate leaves this reorder invisible to the screen-design
    // funnel — no autosave hash change and no collab op is ever produced, so the new
    // order silently reverts. Dispatch like every other action mutation does.
    this.props.sd.setScreenDesign(this.props.sd.getScreenDesign(), ESdChangeReason.DiActionUpdated);
  };

  reorderScheduledJobsEventActions = (result: any, sourceIdx: number, destIdx: number) => {
    const sJob = this.props.sd.getSelectedSj();
    const sJobEvents: any = this.getScheduledJobEvents();
    if (!sJob) return;
    const allEvents = sJobEvents;
    if (result.source.droppableId.substring(10) == result.destination.droppableId.substring(10)) {
      const result = Array.from(allEvents.actions);
      const [removed] = result.splice(sourceIdx, 1);
      result.splice(destIdx, 0, removed);
      allEvents.actions = result;
    } else {
      const sourceName = result.destination.droppableId.substring(10);
      const destinationName = result.source.droppableId.substring(10);

      const sourcesToEdit = allEvents.filter((x) => x.eventName === sourceName);
      const destinationsToEdit = allEvents.filter((x) => x.eventName === destinationName);

      if (sourcesToEdit.length > 0 && destinationsToEdit.length > 0) {
        const sourceToEdit = sourcesToEdit[0];
        const destinationToEdit = destinationsToEdit[0];

        const result1 = Array.from(sourceToEdit.actions);
        const result2 = Array.from(destinationToEdit.actions);

        const [removed] = result2.splice(sourceIdx, 1);
        result1.splice(destIdx, 0, removed);
        sourceToEdit.actions = result1;
        destinationToEdit.actions = result2;
      }
    }
    this.forceUpdate();
  };

  reorderWorkFlowStepEventActions = (result: any, sourceIdx: number, destIdx: number) => {
    const step = this.getWfStepEvents();

    if (!step) return;

    const source = step.actions[sourceIdx];

    step.actions.splice(sourceIdx, 1);
    step.actions.splice(destIdx, 0, source);

    this.props.sd.updateWfStep(step as any);
  };

  reorderBluetoothEventActions = (sourceIdx: number, destIdx: number) => {
    const bleEvent = this.getBleEvents();
    if (!bleEvent) return;

    const source = bleEvent.actions[sourceIdx];

    bleEvent.actions.splice(sourceIdx, 1);
    bleEvent.actions.splice(destIdx, 0, source);
    this.props.sd.updateSelectedBluetoothMethodActions(bleEvent.actions);
  };

  reorderApiBuilderEventActions = (sourceIdx: number, destIdx: number) => {
    const apiEvent = this.getApiEvents();
    if (!apiEvent) return;
    const source = apiEvent.actions[sourceIdx];

    apiEvent.actions.splice(sourceIdx, 1);
    apiEvent.actions.splice(destIdx, 0, source);
    this.forceUpdate();
  };

  getCascaderOption = (val: string): CascaderOptionType[] => {
    const searchedOptions = [...this.state.options];
    if (val) {
      const searchedValue = this.searchData(searchedOptions, val);
      return searchedValue;
    }
    return this.state.options;
  };

  searchData(dataArray, searchTerm) {
    return dataArray.flatMap((obj) => {
      const objHasSearchTerm = Object.entries(obj).some(([key, value]) => {
        if (key !== "children") {
          if (key === "label" && React.isValidElement(value)) {
            // For React elements, check if the category value includes the search term
            return obj.value ? String(obj.value).toLowerCase().includes(searchTerm.toLowerCase()) : false;
          }
          return String(value).toLowerCase().includes(searchTerm.toLowerCase());
        }
        return false;
      });

      if (objHasSearchTerm && !obj.children) return [obj];

      const matchedChildren = this.searchData(obj.children ?? [], searchTerm);
      return objHasSearchTerm || matchedChildren.length > 0
        ? [
            {
              ...obj,
              children: matchedChildren
            }
          ]
        : [];
    });
  }

  handlePasteAction = (): void => {
    const sd: IScreenDesign = this.props.sd.getScreenDesign();
    const clipboardValue = this.props.context.getClipboardValue("action") as IActionClipboard | undefined;
    if (!clipboardValue) return;
    const { action } = clipboardValue;

    const diToAdd: IDisplayItem | undefined = clipboardValue.navigationDI;
    const copyAction: IAction = _.cloneDeep(action);

    if (!copyAction) {
      return;
    }

    ActionService.setNewIdsForAction(copyAction);
    rebindDesignerPastedAction(copyAction, this.props.sd, clipboardValue);

    if (diToAdd) {
      const newDI = ScreenDesignHelper.renewDisplayItem(diToAdd, this.props.sd.screenDesignDictionary);
      newDI.parentId = SpecialDIIds.rootDI;

      if (newDI.friendlyName) {
        newDI.friendlyName = ScreenDesignHelper.incrementFriendlyName(newDI.friendlyName);
      }

      copyAction.parameters.forEach((param: IActionParameter) => {
        if (param.name === "navigationKuikaId") {
          param.symbol.fixedValue = newDI.id;
        }
      });

      const rootDiIdx: number = ScreenDesignHelper.getRootDiIdxInDisplayItems(sd.displayItems);
      if (rootDiIdx >= 0) sd.displayItems[rootDiIdx].children.push(newDI);
    }

    this.createActionByEventName(copyAction, clipboardValue.eventName, true);

    if (diToAdd) {
      this.props.sd.setScreenDesign(sd, ESdChangeReason.DiActionUpdated);
    }
  };

  getOtherDIsNonEmptyEvents = () => {
    const sd = this.props.sd.getScreenDesign();
    const result = [] as IOtherDIsNonEmptyEvents[];
    this.getOtherDIsNonEmptyEventsRecursively(sd.displayItems, result);
    return result;
  };

  getMasterDIsNonEmptyEvents = () => {
    const masterSd = this.props.sd.masterSd;
    const result = [] as IOtherDIsNonEmptyEvents[];
    if (masterSd) {
      this.getOtherDIsNonEmptyEventsRecursively(masterSd.displayItems, result);
    }
    return result;
  };

  getOtherDIsNonEmptyEventsRecursively = (
    DIs: IDisplayItem[],
    result: IOtherDIsNonEmptyEvents[]
  ): IOtherDIsNonEmptyEvents[] => {
    const selectedDisplayItem = this.getSelectedDisplayItem();
    DIs?.forEach((di) => {
      const eventNames = [] as string[];
      (di.item as IDisplayItemComponent)?.events?.forEach((evt) => {
        if (evt.actions?.length > 0) {
          eventNames.push(evt.eventName);
        }
      });
      if (eventNames.length > 0 && di.id !== selectedDisplayItem?.id) {
        result.push({
          diId: di.id,
          diFriendlyName: di.friendlyName,
          eventNames
        });
      }

      if (di?.children?.length > 0) {
        this.getOtherDIsNonEmptyEventsRecursively(di.children, result);
      }
    });

    return result;
  };

  getTriggerCategoryDefinitions = (): CascaderOptionType[] => {
    const items = this.getOtherDIsNonEmptyEvents();
    let masterItems = this.getMasterDIsNonEmptyEvents();
    masterItems = masterItems.map((x) => {
      return {
        ...x,
        diFriendlyName: `${x.diFriendlyName} (Master)`
      } as IOtherDIsNonEmptyEvents;
    });
    const allItems = [...items, ...masterItems];
    return allItems
      .map((x) => {
        const children = x.eventNames.map((eventName) => {
          return {
            category: EActionNodeCategory.triggerEventAction,
            label: eventName,
            value: eventName,
            children: undefined
          } as CascaderOptionType;
        });

        let label = x.diFriendlyName ? x.diFriendlyName : x.diId;
        let value: string | number = x.diId;
        if (x.diId === -1) {
          if (x.diFriendlyName?.includes("Master")) {
            value = `${value.toString()}_master`;
            label = "Master Screen";
          } else label = "Screen";
        }
        return {
          label,
          value,
          children
        } as CascaderOptionType;
      })
      .sort((n1: CascaderOptionType, n2: CascaderOptionType) => {
        const label1 = this.getStringValueForSort(n1);
        const label2 = this.getStringValueForSort(n2);
        return label1.localeCompare(label2, undefined, { sensitivity: "base" });
      });
  };

  setClipboardValue = (value: string) => {
    this.setState({ clipboardValue: value });
  };

  render() {
    if (!this.props || !this.props.sd) {
      return <></>;
    }
    const selectedDisplayItem = this.getSelectedDisplayItem();
    if (!selectedDisplayItem) {
      return <></>;
    }
    const documentParameterEvents = this.getDocumentParameterEvents();
    const sJobs = this.getScheduledJobEvents();
    const apis = this.getApiEvents();
    const wfStepEvents = this.getWfStepEvents();
    const bleEvents = this.getBleEvents();

    const events = this.getEvents();
    const clipboardValue = this.state.clipboardValue || this.props.context.getClipboardValue("action");

    let isPasteButtonVisible = !!clipboardValue;

    if (isPasteButtonVisible) {
      const curDashboard = this.props.sd.dashboardState;

      if (
        curDashboard === DashboardState.scheduledJobs ||
        curDashboard === DashboardState.apiManagement ||
        curDashboard === DashboardState.workFlow
      ) {
        const coppiedActionDef = this.props.sd.actionDefinitions.find(
          (def) => def.id === clipboardValue?.action?.actionDefinitionId
        );

        if (
          coppiedActionDef?.actionType === EDatasourceType.Mqtt ||
          coppiedActionDef?.actionType === EDatasourceType.BleService ||
          coppiedActionDef?.actionType === EDatasourceType.Blockly ||
          coppiedActionDef?.actionType === EDatasourceType.JavaScriptAction ||
          coppiedActionDef?.actionType === EDatasourceType.PythonAction ||
          coppiedActionDef?.actionType === EDatasourceType.SystemJSFunctions ||
          coppiedActionDef?.actionCategory === EActionCategory.Navigation ||
          coppiedActionDef?.name === "DownloadReport"
        ) {
          isPasteButtonVisible = false;
        }
      }
    }

    const options = this.state.eventItems;

    if (!options || options?.length === 0) {
      return <></>;
    }
    const hasAccessDataBinding = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.BindDataToComponent,
      this.props.sd.project?.projectAccess
    );
    if (!hasAccessDataBinding) {
      return <></>;
    }
    const actionCount: number = this.getActionCount(
      documentParameterEvents,
      sJobs,
      apis,
      wfStepEvents,
      bleEvents,
      events
    );
    return (
      <>
        <div className="kuika_Wrapper">
          <div
            // Static regardless of popup state: the search input lives in the popup header now, so
            // opening the picker must not re-flow this row. Padding/top-margin are classes, not an
            // inline style, so the design column can zero the top margin once its own divider
            // supplies the space (action-section.scss).
            className={[
              "kuika_actionPanelWrapperHeader",
              "kuika_actionPanelWrapperHeader_actions",
              "kuika-action-section__header",
              this.props.sd.dashboardState === DashboardState.workFlow ? "kuika-action-section__header--flush" : "",
              this.props.sd.dashboardState === DashboardState.design ? "kuika-action-section__header--actions-top" : ""
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {/* hideHeader only ever suppresses the title in the workflow branch — every other host always shows it.
                customHeader must be honoured in every branch, not just workflow: every other host that passes one —
                the Document Parameters tool's "Source Actions" — used to silently render "Actions" instead. */}
            <div className="kuika-action-section__header-row">
              {(this.props.sd.dashboardState !== DashboardState.workFlow || this.props.hideHeader !== true) && (
                <ActionSectionTitle title={this.props.customHeader ?? "Actions"} count={actionCount} />
              )}

              <div className="kuika-action-section__add-slot">
                {this.props.preventAddAction === true ? (
                  <></>
                ) : (
                  <span
                    // On the "+" alone: on the slot, hovering paste raised this hint beside paste's own tooltip.
                    data-onboarding-id="properties_action-picker_add-action"
                    className={
                      this.props.sd.dashboardState === DashboardState.workFlow
                        ? "kuika_workflow-properties__primary-text kuika_addNavigationLink"
                        : "kuika_addNavigationLink"
                    }
                    id={
                      this.props.sd.dashboardState === DashboardState.workFlow
                        ? "kuika_workflow__add-action-button"
                        : "kuika__add-action-button"
                    }
                    data-testid="actionPickerTrigger"
                    style={{
                      marginLeft: 0,
                      display: "flex"
                    }}
                  >
                    <ActionPickerPopup
                      projectType={this.props.sd.project?.projectType}
                      options={this.state.options}
                      open={this.state.popupVisible}
                      onOpenChange={this.getOptions}
                      onPick={this.handlePick}
                      getActionDefinition={this.getActionDefinitionById}
                      getLockReason={this.getActionLockReason}
                    >
                      <ActionSectionAddButton ariaLabel="Add action" testId="addActionButton" />
                    </ActionPickerPopup>
                  </span>
                )}

                {this.props.preventAddAction === true ? (
                  <></>
                ) : (
                  isPasteButtonVisible && (
                    // Same tinted icon button as the "+", so the header's two controls read as one set.
                    <KuikaTooltip content="Paste action" position="top">
                      <ActionSectionAddButton
                        className="kuika-action-section__paste"
                        iconName="paste"
                        ariaLabel="Paste action"
                        testId="pasteActionButton"
                        data-onboarding-id="properties_action-picker_paste-action"
                        onClick={this.handlePasteAction}
                      />
                    </KuikaTooltip>
                  )
                )}
              </div>
            </div>
          </div>
          <div className="kuika-action-section__events">
            {/* One empty state for every host — screen, scheduled job, API method, workflow step, BLE
                method, document parameters — each of which otherwise renders an empty list. */}
            {actionCount === 0 && this.props.preventAddAction !== true && (
              <ActionSectionEmptyState
                message="No actions yet."
                actionLabel="Add action"
                testId="actionsEmptyState"
                onAdd={() => {
                  if (!this.state.popupVisible) this.getOptions(true);
                }}
              />
            )}
            {this.props.sd.isDocumentParameterAuthoringActive() ? (
              <DragDropContext
                onDragEnd={(result) => {
                  if (result.destination?.index !== null && result.destination?.index !== undefined) {
                    this.props.sd.reorderDocumentParameterActions(result.source.index, result.destination.index);
                  }
                }}
              >
                <EventPanel
                  key="document-parameters-event-panel"
                  event={documentParameterEvents}
                  eventName="documentParametersInit"
                  getActionDefinitionItems={() => this.generateActionDefinitionItems()}
                  clipboardValue={this.state.clipboardValue}
                  setClipboardValue={this.setClipboardValue}
                />
              </DragDropContext>
            ) : this.props.sd?.getSelectedSj()?.id && this.props.sd.dashboardState === DashboardState.scheduledJobs ? (
              <DragDropContext
                onDragEnd={(result) => {
                  if (result.destination?.index !== null && result.destination?.index !== undefined) {
                    this.reorderScheduledJobsEventActions(result, result.source.index, result.destination.index);
                  }
                }}
              >
                <EventPanel
                  key="sj-event-panel"
                  event={sJobs}
                  eventName="scheduledJobInit"
                  getActionDefinitionItems={() => this.generateActionDefinitionItems()}
                  clipboardValue={this.state.clipboardValue}
                  setClipboardValue={this.setClipboardValue}
                />
              </DragDropContext>
            ) : this.props.sd?.getSelectedApi()?.id &&
              this.props.sd?.getSelectedApiMethod()?.id &&
              this.props.sd.dashboardState === DashboardState.apiManagement ? (
              <>
                <DragDropContext
                  onDragEnd={(result) => {
                    if (result.destination?.index !== null && result.destination?.index !== undefined) {
                      this.reorderApiBuilderEventActions(result.source.index, result.destination.index);
                    }
                  }}
                >
                  <EventPanel
                    key="api-event-panel"
                    event={apis}
                    eventName="apiMethodInit"
                    getActionDefinitionItems={() => this.generateActionDefinitionItems()}
                    clipboardValue={this.state.clipboardValue}
                    setClipboardValue={this.setClipboardValue}
                  />
                </DragDropContext>
                {this.shouldShowHtmlReturnTypeWarning() && (
                  <KuikaInfoBox
                    color="yellow"
                    leftUnit={<KuikaSvg name="alert-triangle" type="common" width={16} height={16} />}
                    rightUnit={
                      <span style={{ fontSize: 11, color: "var(--dark-7)" }}>
                        The last action must return a string type for HTML responses.
                      </span>
                    }
                    style={{ margin: "8px 12px" }}
                  />
                )}
              </>
            ) : this.props.sd.selectedWf?.id &&
              this.props.sd.getSelectedWfStep()?.id &&
              this.props.sd.dashboardState === DashboardState.workFlow ? (
              <DragDropContext
                onDragEnd={(result) => {
                  if (result.destination?.index !== undefined) {
                    this.reorderWorkFlowStepEventActions(result, result.source.index, result.destination.index);
                  }
                }}
              >
                <EventPanel
                  key="wf-event-panel"
                  event={wfStepEvents}
                  eventName="workFlowStepInit"
                  getActionDefinitionItems={() => this.generateActionDefinitionItems()}
                  clipboardValue={this.state.clipboardValue}
                  setClipboardValue={this.setClipboardValue}
                  preventActionRemoval={this.props.preventActionRemoval}
                />
              </DragDropContext>
            ) : this.props.sd.activeEditingDatasource?.bluetoothInfo &&
              this.props.sd.getSelectedBluetoothMethod()?.id &&
              this.props.sd.dashboardState === DashboardState.dataDiagramScheme ? (
              <DragDropContext
                onDragEnd={(result) => {
                  if (result.destination?.index !== undefined) {
                    this.reorderBluetoothEventActions(result.source.index, result.destination.index);
                  }
                }}
              >
                <EventPanel
                  key="ble-event-panel"
                  event={bleEvents}
                  eventName="bluetoothMethodInit"
                  getActionDefinitionItems={() => this.generateActionDefinitionItems()}
                  clipboardValue={this.state.clipboardValue}
                  setClipboardValue={this.setClipboardValue}
                />
              </DragDropContext>
            ) : (
              <DragDropContext
                onDragEnd={(result) => {
                  if (
                    !result.source?.droppableId?.includes("Recurring") &&
                    result.destination?.droppableId?.includes("Recurring")
                  ) {
                    return;
                  }
                  if (
                    result.source?.droppableId?.includes("Recurring") &&
                    !result.destination?.droppableId?.includes("Recurring")
                  ) {
                    return;
                  }
                  if (result.destination?.index !== null && result.destination?.index !== undefined) {
                    this.reorderEventActions(result, result.source.index, result.destination.index);
                  }
                }}
              >
                {events
                  .filter((e) => e?.actions?.length > 0)
                  .map((event) => {
                    return (
                      <EventPanel
                        key={`event-${event.eventName}`}
                        event={event}
                        getActionDefinitionItems={() => this.generateActionDefinitionItems()}
                        getUpdatedActionDefinitionItems={() => this.generateActionDefinitionItems()}
                        eventName={event.eventName}
                        clipboardValue={this.state.clipboardValue}
                        setClipboardValue={this.setClipboardValue}
                      />
                    );
                  })}
              </DragDropContext>
            )}
            <div
              ref={(el) => {
                this.bottomRef = el;
              }}
            ></div>
          </div>
        </div>
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.WFStepMappingChange ||
      changeReason === ESdChangeReason.SaveWorkflow ||
      changeReason === ESdChangeReason.ClickWfDesignXmlNode
    ) {
      return true;
    }
    return false;
  };
}
const mapDispatchToProps = (dispatch) => {
  return {
    showDomainAccessRequiredModal: (accessName) => dispatch(showDomainAccessRequiredModal(accessName))
  };
};

const actionPicker = connect(
  null,
  mapDispatchToProps
)(withContext<IActionPickerProps>(withSD<IActionPickerProps>(ActionPicker)));
// Unwrapped class (no redux/SD/context HOC) — for unit tests only, matching the convention in
// `data-diagram/create-connection.tsx`. It is how the add path can be driven against a screen the
// designer does not have open, which is otherwise only reachable by hand (KOA-18431).
export { ActionPicker as ActionPickerClass };
export { actionPicker as ActionPicker };
